
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  watch
} from "vue";
import RntrcPixBloco from "./RntrcPixBloco.vue";
import RntrcOutroServico from "./RntrcOutroServico.vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { formatDateTime } from "../../modules/utils";
import { useToast } from "../../modules/message";
import { useRouter } from "vue-router";
import {
  useRntrcSolicitacaoService,
  podeNaSolicitacao
} from "../../modules/business/rntrc/rntrcSolicitacaoBusiness";
import {
  DocumentoRntrc,
  FlagOption,
  RntrcSolicitacaoPayload,
  RntrcSolicitacaoVeiculoPayload,
  SituacaoRntrc,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  getCodigoErroLabel,
  getDocumentoLabel,
  getMotivoCancelamentoLabel,
  getServicoVeiculoLabel,
  getSituacaoCor,
  getSituacaoLabel,
  getTipoServicoLabel
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcDetalheState {
  aberto: boolean;
  entity: RntrcSolicitacaoPayload | null;
  imagemErroUrl: string | null;
  carregandoImagemErro: boolean;
  outroServicoAberto: boolean;
  /** Baixar todos: montando e gravando o .zip. */
  baixandoTodos: boolean;
}

/** Uma linha do custo total: a própria solicitação ou uma exclusão da frota atual. */
interface LinhaCusto {
  chave: string;
  descricao: string;
  situacao?: SituacaoRntrc | null;
  antt: number | null;
}

/** Um PDF do "Baixar todos": da solicitação ou de uma placa (consulta de veículo). */
interface ArquivoParaBaixar {
  tipo: DocumentoRntrc;
  veiculo?: RntrcSolicitacaoVeiculoPayload;
}

/** O "Salvar como" da File System Access API (Chrome e Edge). */
interface ArquivoGravavel {
  write(dados: BufferSource | Blob): Promise<void>;
  close(): Promise<void>;
}

interface ArquivoEscolhido {
  createWritable(): Promise<ArquivoGravavel>;
}

type JanelaComSalvarComo = Window & {
  showSaveFilePicker?: (opcoes: {
    suggestedName?: string;
    startIn?: string;
    types?: Array<{
      description: string;
      accept: Record<string, Array<string>>;
    }>;
  }) => Promise<ArquivoEscolhido>;
};

export default defineComponent({
  emits: ["close", "acao"],
  setup(props, { emit }) {
    const state = reactive<RntrcDetalheState>({
      aberto: true,
      entity: null,
      imagemErroUrl: null,
      carregandoImagemErro: false,
      outroServicoAberto: false,
      baixandoTodos: false
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const router = useRouter();

    const podeCriar = computed(() => !!amIAllowed("create", "rntrc"));

    /** Depois de ver uma solicitação, o cliente já parte para a próxima. */
    const novaSolicitacao = () => {
      router.push({ name: "rntrc-solicitacao-new" });
    };

    const fechar = () => {
      state.aberto = false;
      emit("close", props.idRntrcSolicitacao);
    };

    const carregar = () => {
      const { data, buscar } = useRntrcSolicitacaoService();
      buscar(props.idRntrcSolicitacao)
        .then(() => {
          state.entity = data.value ?? null;
        })
        .catch(() => {
          fechar();
        });
    };

    const emitAcao = (acao: string) => {
      emit("acao", acao, state.entity);
    };

    const isConsulta = computed(() => state.entity?.flTipoServico === "CVE");

    const isConsultaTransportador = computed(
      () => state.entity?.flTipoServico === "CTR"
    );

    const semCadastroNaConsulta = computed(
      () => isConsultaTransportador.value && state.entity?.flSituacao === "TSC"
    );

    const ehCpf = computed(
      () => (state.entity?.cpfCnpj ?? "").replace(/\D/g, "").length === 11
    );

    const emPagamento = computed(
      () =>
        state.entity?.flSituacao === "PAG" || state.entity?.flSituacao === "PGI"
    );

    const mostrarPix = computed(
      () =>
        emPagamento.value ||
        (!!state.entity?.codigoPix &&
          state.entity?.flSituacao !== "CON" &&
          state.entity?.flSituacao !== "CAN")
    );

    const placasPresas = computed(() =>
      (state.entity?.veiculos ?? []).filter((v) => v.flExclusaoNecessaria)
    );

    const placasEmCancelamento = computed(() =>
      (state.entity?.veiculos ?? [])
        .filter((v) => v.flCancelamentoSolicitado && !v.dataCancelamento)
        .map((v) => formatPlaca(v.placa))
        .join(", ")
    );

    const labelTransportador = computed(() => {
      if (state.entity?.flTipoServico === "EXC") {
        return "CPF/CNPJ de quem tem a placa hoje";
      } else if (state.entity?.flTipoServico === "IAR") {
        return "Transportador (arrendatário)";
      }
      return "Transportador";
    });

    const documentos = computed(() => {
      const docs = [] as Array<FlagOption<DocumentoRntrc>>;
      const e = state.entity;
      // consulta de veículo: extrato e certificado ficam na linha de cada placa
      const porPlaca =
        isConsulta.value &&
        (e?.veiculos ?? []).some((v) => v.possuiExtrato || v.possuiCertificado);
      if (e?.possuiExtrato && !porPlaca) {
        docs.push({ flag: "EXT", descricao: getDocumentoLabel("EXT") });
      }
      if (e?.possuiCertificado && !porPlaca) {
        docs.push({ flag: "CER", descricao: getDocumentoLabel("CER") });
      }
      if (e?.possuiRelatorio) {
        docs.push({ flag: "REL", descricao: getDocumentoLabel("REL") });
      }
      if (e?.possuiComprovante) {
        docs.push({ flag: "COM", descricao: getDocumentoLabel("COM") });
      }
      return docs;
    });

    const abrirDocumento = (tipo: DocumentoRntrc, newTab?: boolean) => {
      const { abrirDocumento } = useRntrcSolicitacaoService();
      abrirDocumento(props.idRntrcSolicitacao, tipo, newTab);
    };

    const abrirDocumentoVeiculo = (
      v: RntrcSolicitacaoVeiculoPayload,
      tipo: "EXT" | "CER",
      newTab?: boolean
    ) => {
      if (!v.idRntrcSolicitacaoVeiculo) {
        return;
      }
      const { abrirDocumentoVeiculo } = useRntrcSolicitacaoService();
      abrirDocumentoVeiculo(
        props.idRntrcSolicitacao,
        v.idRntrcSolicitacaoVeiculo,
        tipo,
        newTab
      );
    };

    /** As placas que os documentos do pedido cobrem: as incluídas; nenhuma marcada, todas. */
    const placasDosDocumentos = computed(() => {
      const veiculos = state.entity?.veiculos ?? [];
      const incluidas = veiculos.filter(
        (v) => v.flIncluido !== false && !v.dataCancelamento
      );
      return incluidas.length ? incluidas : veiculos;
    });

    /**
     * Tudo o que o .zip leva, com o nome que a API dá (PLACA-extrato.pdf / PLACA-certificado.pdf):
     * na consulta de veículo, os de cada placa; nos demais, o extrato e o certificado do
     * transportador uma vez por placa do pedido, e os outros documentos uma vez.
     */
    const arquivosParaBaixar = computed<Array<ArquivoParaBaixar>>(() => {
      const lista = [] as Array<ArquivoParaBaixar>;
      if (isConsulta.value) {
        (state.entity?.veiculos ?? []).forEach((v) => {
          if (v.possuiExtrato) {
            lista.push({ tipo: "EXT", veiculo: v });
          }
          if (v.possuiCertificado) {
            lista.push({ tipo: "CER", veiculo: v });
          }
        });
      }
      documentos.value.forEach((d) => {
        const porPlaca =
          !isConsulta.value && (d.flag === "EXT" || d.flag === "CER");
        if (porPlaca && placasDosDocumentos.value.length) {
          placasDosDocumentos.value.forEach((v) =>
            lista.push({ tipo: d.flag, veiculo: v })
          );
        } else {
          lista.push({ tipo: d.flag });
        }
      });
      return lista;
    });

    const nomeDoZip = () =>
      "rntrc-" + props.idRntrcSolicitacao + "-documentos.zip";

    /** Download comum do arquivo que já está na memória. */
    const baixarComoDownload = (dados: ArrayBuffer, nome: string) => {
      const url = URL.createObjectURL(
        new Blob([dados], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    /**
     * Um .zip com todos os PDFs (cada um com o nome da placa). Chrome e Edge abrem o "Salvar
     * como" para escolher onde gravar; nos demais navegadores vai como um download comum.
     */
    const baixarTodos = async () => {
      const salvarComo = (window as JanelaComSalvarComo).showSaveFilePicker;
      if (!salvarComo) {
        state.baixandoTodos = true;
        useRntrcSolicitacaoService()
          .abrirZipDocumentos(props.idRntrcSolicitacao)
          .finally(() => (state.baixandoTodos = false));
        return;
      }
      let destino: ArquivoEscolhido;
      try {
        // a janela abre antes do download: o navegador só a permite logo depois do clique
        destino = await salvarComo.call(window, {
          suggestedName: nomeDoZip(),
          startIn: "downloads",
          types: [
            {
              description: "Arquivo .zip",
              accept: { "application/zip": [".zip"] }
            }
          ]
        });
      } catch (e) {
        // fechou o "Salvar como" sem escolher
        return;
      }
      state.baixandoTodos = true;
      const service = useRntrcSolicitacaoService();
      try {
        await service.buscarZipDocumentos(props.idRntrcSolicitacao);
      } catch (e) {
        // o aviso do erro da API já aparece
        state.baixandoTodos = false;
        return;
      }
      const zip = service.data.value as ArrayBuffer;
      try {
        const gravavel = await destino.createWritable();
        await gravavel.write(zip);
        await gravavel.close();
        useToast().success(
          arquivosParaBaixar.value.length + " documento(s) salvos no .zip."
        );
      } catch (e) {
        // o navegador recusou gravar no local escolhido: o .zip já está pronto, vai como download
        baixarComoDownload(zip, nomeDoZip());
        useToast().warning(
          "O navegador não deixou gravar no local escolhido; o .zip foi para a pasta de downloads dele."
        );
      } finally {
        state.baixandoTodos = false;
      }
    };

    /**
     * Custo total quando a solicitação precisou de exclusão da frota atual: a taxa da ANTT
     * (o valor do site, que é o cobrado do cliente) dela e de cada exclusão feita no caminho.
     */
    const custoDaOperacao = computed(() => {
      const e = state.entity;
      const exclusoes = (e?.exclusoes ?? []).filter(
        (x) =>
          x.flSituacao !== "CAN" && x.flSituacao !== "ERR" && x.valorPix != null
      );
      if (!e || !exclusoes.length) {
        return null;
      }
      const linhas = [
        {
          chave: "solicitacao",
          descricao:
            getTipoServicoLabel(e.flTipoServico) +
            " (nº " +
            e.idRntrcSolicitacao +
            ")",
          situacao: e.flSituacao,
          antt: e.valorPix ?? null
        }
      ] as Array<LinhaCusto>;
      exclusoes.forEach((x) => {
        const placas = (x.veiculos ?? []).map((v) => formatPlaca(v.placa));
        linhas.push({
          chave: "exclusao-" + x.idRntrcSolicitacao,
          descricao: "Exclusão da frota atual — " + placas.join(", "),
          situacao: x.flSituacao,
          antt: x.valorPix ?? null
        });
      });
      return {
        linhas,
        antt: linhas.reduce((total, l) => total + (l.antt ?? 0), 0)
      };
    });

    const carregarImagemErro = () => {
      const { data, linkDocumento } = useRntrcSolicitacaoService();
      state.carregandoImagemErro = true;
      linkDocumento(props.idRntrcSolicitacao, "ERR")
        .then(() => {
          state.imagemErroUrl = data.value ?? null;
        })
        .finally(() => {
          state.carregandoImagemErro = false;
        });
    };

    // a lista incrementa a versão depois de uma ação (confirmar, reenviar, cancelar)
    watch(
      () => props.versao,
      () => carregar()
    );

    onMounted(() => {
      carregar();
    });

    return {
      amIAllowed,
      podeNaSolicitacao,
      fechar,
      emitAcao,
      isConsulta,
      isConsultaTransportador,
      semCadastroNaConsulta,
      ehCpf,
      emPagamento,
      mostrarPix,
      placasEmCancelamento,
      placasPresas,
      labelTransportador,
      documentos,
      abrirDocumento,
      abrirDocumentoVeiculo,
      arquivosParaBaixar,
      baixarTodos,
      custoDaOperacao,
      podeCriar,
      novaSolicitacao,
      carregarImagemErro,
      formatDateTime,
      formatCpfCnpj,
      formatMoeda,
      formatPlaca,
      getCodigoErroLabel,
      getMotivoCancelamentoLabel,
      getServicoVeiculoLabel,
      getSituacaoCor,
      getSituacaoLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    RntrcPixBloco,
    RntrcOutroServico
  },
  props: {
    idRntrcSolicitacao: {
      type: Number,
      required: true
    },
    versao: {
      type: Number,
      default: 0
    }
  }
});
